import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
  Alert,
  Progress,
  Spinner,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Table,
  CustomInput,
  Pagination,
  PaginationItem,
  PaginationLink
} from 'reactstrap';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../redux/QuestionBank/SubjectsUnitsAction';
import { createQuestion, fetchQuestions, updateQuestion, deleteQuestion, deleteQuestionsBatch } from '../../redux/QuestionBank/QuestionsAction';
import { fetchPassages } from '../../redux/QuestionBank/PassageAction';
import { bulkResolveFeedback } from '../../redux/QuestionBank/FeedbackAction';
import { uploadBatchFile, saveUploadLog } from '../../redux/QuestionBank/UploadLogsAction';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { draftRawToPlainText, textToDraftRawWithImage } from '../../util/draftToHtml';
import { uploadImage } from '../../util/imageUpload';
import { optimizeImage } from '../../util/imageOptimize';
import BulkUploadReviewModal from './BulkUploadReviewModal';

// Dynamic choice support constants
const MAX_CHOICES = 10;
const CHOICE_LABELS = Array.from({ length: MAX_CHOICES }, (_, i) => String.fromCharCode(65 + i)); // ['A', ..., 'J']
const CORRECT_ANSWER_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [label, i.toString()]));
const ID_TO_LABEL_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [i.toString(), label]));
const ANSWER_DROPDOWN_STRING = CHOICE_LABELS.join(','); // "A,B,C,D,E,F,G,H,I,J"
const FIRST_CHOICE_COL = 5; // 0-indexed column of Choice A in upload template (col 4 = Passage Title)
const FIRST_CHOICE_COL_EXPORT = 6; // 0-indexed column of Choice A in export template (col 0 = Question Code, col 5 = Passage Title)

// Image column constants (upload template) — alternating: image, width, image, width, ...
const TAGS_COL = FIRST_CHOICE_COL + MAX_CHOICES + 3; // Tags column
const QUESTION_IMAGE_COL = TAGS_COL + 1;
const QUESTION_IMAGE_WIDTH_COL = QUESTION_IMAGE_COL + 1;
const FIRST_CHOICE_IMAGE_COL = QUESTION_IMAGE_WIDTH_COL + 1; // stride 2: choice i image = +i*2, width = +i*2+1
const EXPLANATION_IMAGE_COL = FIRST_CHOICE_IMAGE_COL + MAX_CHOICES * 2;
const EXPLANATION_IMAGE_WIDTH_COL = EXPLANATION_IMAGE_COL + 1;

// Image column constants (export template — offset by 1 for Question Code column)
const TAGS_COL_EXPORT = FIRST_CHOICE_COL_EXPORT + MAX_CHOICES + 3;
const QUESTION_IMAGE_COL_EXPORT = TAGS_COL_EXPORT + 1;
const QUESTION_IMAGE_WIDTH_COL_EXPORT = QUESTION_IMAGE_COL_EXPORT + 1;
const FIRST_CHOICE_IMAGE_COL_EXPORT = QUESTION_IMAGE_WIDTH_COL_EXPORT + 1;
const EXPLANATION_IMAGE_COL_EXPORT = FIRST_CHOICE_IMAGE_COL_EXPORT + MAX_CHOICES * 2;
const EXPLANATION_IMAGE_WIDTH_COL_EXPORT = EXPLANATION_IMAGE_COL_EXPORT + 1;

const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

const BulkUploadQuestions = ({ userEmail }) => {
  const dispatch = useDispatch();
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);

  const { questions: allQuestions, loading: questionsLoading } = useSelector(state => state.questionBankQuestions);
  const { passages } = useSelector(state => state.questionBankPassages);

  const [file, setFile] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);

  // Image/ZIP state
  const [imageFiles, setImageFiles] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // Bulk Edit state
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [editParsedQuestions, setEditParsedQuestions] = useState([]);
  const [editParseErrors, setEditParseErrors] = useState([]);
  const [isEditReviewModalOpen, setIsEditReviewModalOpen] = useState(false);
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [editUploadProgress, setEditUploadProgress] = useState(0);
  const [editUploadResults, setEditUploadResults] = useState(null);
  const [editSearchTerm, setEditSearchTerm] = useState('');
  const [filterNoTopic, setFilterNoTopic] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [editCurrentPage, setEditCurrentPage] = useState(1);
  const editQuestionsPerPage = 25;

  // Fetch subjects, units, topics, and all questions on mount
  useEffect(() => {
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions({ pageSize: 5000 }));
    dispatch(fetchPassages());
  }, [dispatch]);

  // Template columns definition
  const templateColumns = [
    'Subject Name',
    'Unit Name',
    'Topic Name (Optional)',
    'Passage Title (Optional)',
    'Question Text',
    ...CHOICE_LABELS.map(l => `Choice ${l}`),
    'Correct Answer',
    'Explanation',
    'Lesson Reference (Optional)',
    'Tags (comma-separated, optional)',
    'Question Image (filename)',
    'Question Image Width (px)',
    ...CHOICE_LABELS.flatMap(l => [`Choice ${l} Image (filename)`, `Choice ${l} Image Width (px)`]),
    'Explanation Image (filename)',
    'Explanation Image Width (px)'
  ];

  // Indices of the image columns for orange header styling
  const IMAGE_COL_START_INDEX = templateColumns.indexOf('Question Image (filename)');

  // Helper function to create valid Excel named range name
  const sanitizeNameForExcel = (name) => {
    // Excel named ranges can't start with numbers, can't have spaces or special chars
    return 'R_' + name
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/^[0-9]/, 'N$&')
      .substring(0, 250); // Excel has max length for names
  };

  // Generate and download template with CASCADING dropdowns using ExcelJS
  const downloadTemplate = async () => {
    if (!subjects || subjects.length === 0) {
      Swal.fire('No Data', 'Please wait for subjects to load before downloading the template.', 'warning');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'PMR Prep';
      workbook.created = new Date();

      // Get all data with codes - format as "CODE Name" for display
      const subjectsList = subjects.map(s => ({
        name: s.name,
        code: s.code || '',
        displayName: s.code ? `${s.code} ${s.name}` : s.name
      })).filter(s => s.name).sort((a, b) => a.name.localeCompare(b.name));
      const subjectDisplayNames = subjectsList.map(s => s.displayName);

      const unitsList = (units || []).map(u => {
        const subject = subjects.find(s => s.subjectId === u.subjectId);
        return {
          name: u.name,
          code: u.code || '',
          displayName: u.code ? `${u.code} ${u.name}` : u.name,
          subjectId: u.subjectId,
          subjectName: subject?.name || '',
          subjectDisplayName: subject?.code ? `${subject.code} ${subject.name}` : (subject?.name || '')
        };
      }).filter(u => u.name);

      const topicsList = (topics || []).map(t => {
        const unit = (units || []).find(u => u.unitId === t.unitId);
        return {
          name: t.name,
          code: t.code || '',
          displayName: t.code ? `${t.code} ${t.name}` : t.name,
          unitId: t.unitId,
          unitName: unit?.name || '',
          unitDisplayName: unit?.code ? `${unit.code} ${unit.name}` : (unit?.name || '')
        };
      }).filter(t => t.name);

      // Group units by subject display name and topics by unit display name
      const unitsBySubject = {};
      unitsList.forEach(u => {
        if (!unitsBySubject[u.subjectDisplayName]) {
          unitsBySubject[u.subjectDisplayName] = [];
        }
        unitsBySubject[u.subjectDisplayName].push(u.displayName);
      });

      const topicsByUnit = {};
      topicsList.forEach(t => {
        if (!topicsByUnit[t.unitDisplayName]) {
          topicsByUnit[t.unitDisplayName] = [];
        }
        topicsByUnit[t.unitDisplayName].push(t.displayName);
      });

      // ========== Create Lists Sheet (for named ranges) ==========
      const listsSheet = workbook.addWorksheet('Lists');
      listsSheet.state = 'veryHidden'; // Hide this sheet from users

      // Column A: All subjects (with code - name format)
      let currentCol = 1;
      subjectDisplayNames.forEach((displayName, i) => {
        listsSheet.getCell(i + 1, currentCol).value = displayName;
      });

      // Define named range for Subjects
      workbook.definedNames.add(`Lists!$A$1:$A$${subjectDisplayNames.length}`, 'SubjectsList');

      // Create columns for each subject's units
      currentCol = 2;
      const subjectColMap = {};
      Object.keys(unitsBySubject).sort().forEach(subjectDisplayName => {
        const unitsForSubject = unitsBySubject[subjectDisplayName];
        subjectColMap[subjectDisplayName] = currentCol;

        unitsForSubject.forEach((unitDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = unitDisplayName;
        });

        // Create named range for this subject's units (use display name for INDIRECT lookup)
        const rangeName = sanitizeNameForExcel(subjectDisplayName);
        const colLetter = String.fromCharCode(64 + currentCol);
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${unitsForSubject.length}`, rangeName);

        currentCol++;
      });

      // Create columns for each unit's topics
      const unitColMap = {};
      Object.keys(topicsByUnit).sort().forEach(unitDisplayName => {
        const topicsForUnit = topicsByUnit[unitDisplayName];
        unitColMap[unitDisplayName] = currentCol;

        topicsForUnit.forEach((topicDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = topicDisplayName;
        });

        // Create named range for this unit's topics
        const rangeName = sanitizeNameForExcel('T_' + unitDisplayName);
        // Handle column letters beyond Z
        let colLetter;
        if (currentCol <= 26) {
          colLetter = String.fromCharCode(64 + currentCol);
        } else {
          const first = Math.floor((currentCol - 1) / 26);
          const second = ((currentCol - 1) % 26) + 1;
          colLetter = String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
        }
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${topicsForUnit.length}`, rangeName);

        currentCol++;
      });

      // Create columns for each unit's passages (grouped by unit display name)
      const passagesByUnit = {};
      (passages || []).forEach(p => {
        const unit = (units || []).find(u => u.unitId === p.unitId);
        if (!unit) return;
        const unitDisplayName = unit.code ? `${unit.code} ${unit.name}` : unit.name;
        if (!passagesByUnit[unitDisplayName]) {
          passagesByUnit[unitDisplayName] = [];
        }
        passagesByUnit[unitDisplayName].push(p.title);
      });

      Object.keys(passagesByUnit).sort().forEach(unitDisplayName => {
        const passagesForUnit = passagesByUnit[unitDisplayName];
        passagesForUnit.forEach((title, i) => {
          listsSheet.getCell(i + 1, currentCol).value = title;
        });

        const rangeName = sanitizeNameForExcel('P_' + unitDisplayName);
        let colLetter;
        if (currentCol <= 26) {
          colLetter = String.fromCharCode(64 + currentCol);
        } else {
          const first = Math.floor((currentCol - 1) / 26);
          const second = ((currentCol - 1) % 26) + 1;
          colLetter = String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
        }
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${passagesForUnit.length}`, rangeName);

        currentCol++;
      });

      // ========== Create Reference Sheet ==========
      const refSheet = workbook.addWorksheet('Reference_Data');

      // Headers
      refSheet.getRow(1).values = ['Subject', 'Subject Code', 'Unit', 'Unit Code', 'Topic', 'Topic Code'];
      refSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      refSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };

      // Data - Sort units by subject for easier reading
      const sortedUnits = [...unitsList].sort((a, b) => a.subjectName.localeCompare(b.subjectName));
      const sortedTopics = [...topicsList].sort((a, b) => a.unitName.localeCompare(b.unitName));

      const maxRows = Math.max(subjectsList.length, sortedUnits.length, sortedTopics.length);
      for (let i = 0; i < maxRows; i++) {
        const row = refSheet.getRow(i + 2);
        row.values = [
          subjectsList[i]?.name || '',
          subjectsList[i]?.code || '',
          sortedUnits[i]?.name || '',
          sortedUnits[i]?.code || '',
          sortedTopics[i]?.name || '',
          sortedTopics[i]?.code || ''
        ];
      }

      // Set column widths
      refSheet.columns = [
        { width: 25 },
        { width: 20 },
        { width: 30 },
        { width: 20 },
        { width: 25 },
        { width: 20 }
      ];

      // ========== Create Questions Template Sheet ==========
      const questionsSheet = workbook.addWorksheet('Questions Template');

      // Headers
      questionsSheet.getRow(1).values = templateColumns;
      questionsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      questionsSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };

      // Style image columns with orange header
      const headerRow = questionsSheet.getRow(1);
      for (let ci = IMAGE_COL_START_INDEX; ci < templateColumns.length; ci++) {
        const cell = headerRow.getCell(ci + 1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED7D31' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      // Set column widths
      questionsSheet.columns = [
        { width: 22 },  // Subject
        { width: 28 },  // Unit
        { width: 22 },  // Topic
        { width: 30 },  // Passage Title
        { width: 50 },  // Question Text
        ...CHOICE_LABELS.map(() => ({ width: 30 })),  // Choice A-J
        { width: 15 },  // Correct Answer
        { width: 50 },  // Explanation
        { width: 40 },  // Lesson Reference
        { width: 25 },  // Tags
        { width: 28 }, { width: 18 },  // Question Image + Width
        ...CHOICE_LABELS.flatMap(() => [{ width: 22 }, { width: 18 }]),  // Choice A-J Image + Width
        { width: 28 }, { width: 18 }   // Explanation Image + Width
      ];

      // Add 20 ready rows with defaults
      const correctAnswerCellIndex = FIRST_CHOICE_COL + MAX_CHOICES + 1; // 1-indexed cell for Correct Answer
      for (let i = 2; i <= 21; i++) {
        const row = questionsSheet.getRow(i);
        row.getCell(1).value = subjectDisplayNames[0] || '';  // Subject (with code)
        row.getCell(correctAnswerCellIndex).value = 'A';  // Correct Answer
      }

      // Add data validation (dropdowns) for rows 2-121
      for (let rowNum = 2; rowNum <= 121; rowNum++) {
        // Subject dropdown (Column A) - Static list
        questionsSheet.getCell(`A${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: ['SubjectsList'],
          showErrorMessage: true,
          errorTitle: 'Invalid Subject',
          error: 'Please select a subject from the dropdown list'
        };

        // Unit dropdown (Column B) - DYNAMIC based on Subject in Column A
        // Uses INDIRECT to reference the named range based on Subject value
        // Must match sanitizeNameForExcel: replace all non-alphanumeric with _
        questionsSheet.getCell(`B${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`INDIRECT("R_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(A${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
          showErrorMessage: true,
          errorTitle: 'Invalid Unit',
          error: 'Please select a unit from the dropdown. Make sure to select a Subject first!'
        };

        // Topic dropdown (Column C) - DYNAMIC based on Unit in Column B
        if (Object.keys(topicsByUnit).length > 0) {
          questionsSheet.getCell(`C${rowNum}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`INDIRECT("R_T_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(B${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
            showErrorMessage: true,
            errorTitle: 'Invalid Topic',
            error: 'Please select a topic from the dropdown or leave blank. Make sure to select a Unit first!'
          };
        }

        // Passage Title dropdown (Column D) - DYNAMIC based on Unit in Column B
        if (Object.keys(passagesByUnit).length > 0) {
          questionsSheet.getCell(`D${rowNum}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`INDIRECT("R_P_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(B${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
            showErrorMessage: true,
            errorTitle: 'Invalid Passage',
            error: 'Please select a passage from the dropdown or leave blank. Make sure to select a Unit first!'
          };
        }

        // Correct Answer dropdown
        const correctAnswerCol = String.fromCharCode(65 + FIRST_CHOICE_COL + MAX_CHOICES); // Column letter for Correct Answer
        questionsSheet.getCell(`${correctAnswerCol}${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${ANSWER_DROPDOWN_STRING}"`],
          showErrorMessage: true,
          errorTitle: 'Invalid Answer',
          error: `Please select ${ANSWER_DROPDOWN_STRING}`
        };
      }

      // ========== Create Instructions Sheet ==========
      const instructionsSheet = workbook.addWorksheet('Instructions');

      const instructions = [
        ['Bulk Upload Questions - Instructions'],
        [''],
        ['HOW TO USE THIS TEMPLATE:'],
        [''],
        ['1. Go to the "Questions Template" sheet to enter your questions'],
        ['2. Select a SUBJECT first - this will filter the Unit dropdown!'],
        ['3. Select a UNIT - this will filter the Topic dropdown!'],
        ['4. Optionally enter a Passage Title to link questions to an existing passage'],
        ['5. Fill in the Question, Choices (A and B required, C-J optional), Correct Answer, and Explanation'],
        [''],
        ['CASCADING DROPDOWNS:'],
        ['✓ Subject dropdown shows all available subjects'],
        ['✓ Unit dropdown automatically shows only units for the selected subject'],
        ['✓ Topic dropdown automatically shows only topics for the selected unit'],
        ['✓ Passage Title dropdown shows only passages for the selected unit'],
        [''],
        ['Column Descriptions:'],
        ['- Subject Name: Select from dropdown (required)'],
        ['- Unit Name: Auto-filtered by Subject (required)'],
        ['- Topic Name: Auto-filtered by Unit (optional)'],
        ['- Passage Title: Auto-filtered by Unit. Select an existing passage to link this question to (optional)'],
        ['- Question Text: The question to ask (required)'],
        ['- Choice A-J: Answer options (A and B required, C-J optional)'],
        [`- Correct Answer: Select ${ANSWER_DROPDOWN_STRING} (required)`],
        ['- Explanation: Explanation shown after answering (recommended)'],
        ['- Lesson Reference: Reference to lesson material (optional)'],
        ['- Tags: Comma-separated tags for categorization (optional)'],
        ['- Question Image / Choice Image / Explanation Image: Filename of an image in the /images folder of the ZIP (optional)'],
        ['- Question Image Width / Choice Image Width / Explanation Image Width: Display width in pixels (optional, defaults: question=250, choices=150, explanation=250)'],
        [''],
        ['IMAGE SUPPORT (Optional):'],
        ['To add images to questions, choices, or explanations:'],
        ['1. Create a ZIP file containing your Excel file and an /images folder'],
        ['2. In the image columns, enter just the filename (e.g., "diagram1.png")'],
        ['3. Place the corresponding image files in the /images folder inside the ZIP'],
        ['4. Optionally set image widths in the width columns (e.g., "200" for 200px)'],
        ['4. Upload the .zip file instead of the .xlsx file'],
        [''],
        ['ZIP file structure:'],
        ['  my_questions.zip'],
        ['    ├── questions.xlsx  (your Excel file)'],
        ['    └── images/'],
        ['        ├── diagram1.png'],
        ['        ├── chart2.jpg'],
        ['        └── ...'],
        [''],
        ['Supported image formats: .png, .jpg, .jpeg, .gif, .webp, .svg'],
        [''],
        ['IMPORTANT: If you create new passages, always download a fresh template to get the updated passage dropdowns.'],
        [''],
        ['NOTE: First 20 rows are pre-filled with defaults. You can add more rows as needed.'],
        [''],
        ['AVAILABLE DATA:'],
        [''],
        ['Subjects:'],
        ...subjectDisplayNames.map(s => [`  • ${s}`]),
        [''],
        ['Units by Subject:'],
        ...Object.keys(unitsBySubject).sort().flatMap(subj => [
          [`  ${subj}:`],
          ...unitsBySubject[subj].map(u => [`      → ${u}`])
        ]),
      ];

      instructions.forEach((row, index) => {
        const excelRow = instructionsSheet.getRow(index + 1);
        excelRow.values = row;
        if (index === 0) {
          excelRow.font = { bold: true, size: 16, color: { argb: 'FF4472C4' } };
        } else if (row[0]?.startsWith('HOW TO USE') || row[0]?.startsWith('CASCADING') || row[0]?.startsWith('AVAILABLE')) {
          excelRow.font = { bold: true, size: 12 };
        } else if (row[0]?.startsWith('IMPORTANT')) {
          excelRow.font = { bold: true, size: 11, color: { argb: 'FFDC3545' } };
        }
      });

      instructionsSheet.getColumn(1).width = 80;

      // Reorder sheets - Instructions first
      const sheetsOrder = ['Instructions', 'Questions Template', 'Reference_Data', 'Lists'];
      workbook.eachSheet((sheet) => {
        const index = sheetsOrder.indexOf(sheet.name);
        if (index !== -1) {
          sheet.orderNo = index;
        }
      });

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'question_bank_upload_template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Template Downloaded!',
        html: `
          <p>The template has <strong>cascading dropdowns</strong>:</p>
          <ul style="text-align: left;">
            <li><strong>Subject</strong> → Select first (${subjectDisplayNames.length} options)</li>
            <li><strong>Unit</strong> → Auto-filters by Subject!</li>
            <li><strong>Topic</strong> → Auto-filters by Unit!</li>
            <li><strong>Passage Title</strong> → Auto-filters by Unit!</li>
            <li><strong>Correct Answer</strong> → ${ANSWER_DROPDOWN_STRING}</li>
          </ul>
          <p><strong>20 rows pre-filled</strong> with defaults. Add more as needed.</p>
          <p class="text-warning"><strong>If you add new passages, download a fresh template to update the dropdowns.</strong></p>
        `,
        confirmButtonText: 'Got it!'
      });

    } catch (error) {
      console.error('Error generating template:', error);
      Swal.fire('Error', 'Failed to generate template. Please try again.', 'error');
    }
  };

  // Extract ZIP file and return Excel file + images map
  const extractZip = async (zipFile) => {
    const zip = await JSZip.loadAsync(zipFile);
    let excelFile = null;
    const images = {};

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir || path.startsWith('__MACOSX')) continue;
      const filename = path.split('/').pop();
      if (!filename) continue;
      const ext = filename.split('.').pop().toLowerCase();

      if (['xlsx', 'xls', 'csv'].includes(ext) && !excelFile) {
        const blob = await entry.async('blob');
        excelFile = new File([blob], filename, { type: blob.type });
      } else if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
        const blob = await entry.async('blob');
        images[filename.toLowerCase()] = new File([blob], filename, { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });
      }
    }

    if (!excelFile) throw new Error('No Excel file found in ZIP. Please include an .xlsx, .xls, or .csv file.');
    console.log('[ZIP] Extracted excel:', excelFile.name, 'size:', excelFile.size, 'images:', Object.keys(images));
    return { excelFile, images };
  };

  // Parse uploaded file
  const parseFile = useCallback((file, zipImages = {}) => {
    console.log('[PARSE] parseFile called, file:', file?.name, 'size:', file?.size, 'zipImages:', Object.keys(zipImages));
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        console.log('[PARSE] reader.onload fired, data length:', e.target.result?.byteLength);
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Find the correct sheet - prefer "Questions Template", otherwise use first data sheet
        let sheetName = workbook.SheetNames.find(name =>
          name.toLowerCase().includes('questions') || name.toLowerCase().includes('template')
        );
        // If no questions sheet found, use the first sheet that's not Instructions/Reference/Lists
        if (!sheetName) {
          sheetName = workbook.SheetNames.find(name =>
            !['instructions', 'reference_data', 'lists'].includes(name.toLowerCase())
          ) || workbook.SheetNames[0];
        }

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        console.log('[PARSE] Sheet:', sheetName, 'SheetNames:', workbook.SheetNames, 'Rows:', jsonData.length, 'First row:', jsonData[0], 'Second row:', jsonData[1]);

        if (jsonData.length < 2) {
          setParseErrors(['File is empty or contains only headers']);
          return;
        }

        const headers = jsonData[0];
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));

        const errors = [];
        const questions = [];

        rows.forEach((row, rowIndex) => {
          const rowNum = rowIndex + 2; // Account for header row and 0-indexing

          // Column mapping:
          // 0: Subject, 1: Unit, 2: Topic, 3: Passage Title, 4: Question, 5..5+N-1: Choices A-J, 5+N: Answer, 6+N: Explanation, 7+N: Lesson Reference, 8+N: Tags
          // Note: Values may be in "CODE Name" format, so we match against both full string and name part
          const subjectRaw = row[0]?.toString().trim() || '';
          const unitRaw = row[1]?.toString().trim() || '';
          const topicRaw = row[2]?.toString().trim() || '';
          const passageTitle = row[3]?.toString().trim() || '';

          // Extract name from "CODE Name" format (name is everything after the first space if code exists)
          const subjectName = subjectRaw;
          const unitName = unitRaw;
          const topicName = topicRaw;
          const questionText = row[4]?.toString().trim() || '';
          // Read choices dynamically (columns 4 through 4+MAX_CHOICES-1)
          const choiceTexts = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_COL + i]?.toString().trim() || '');
          const correctAnswer = row[FIRST_CHOICE_COL + MAX_CHOICES]?.toString().trim().toUpperCase() || '';
          const explanation = row[FIRST_CHOICE_COL + MAX_CHOICES + 1]?.toString().trim() || '';
          const lessonReference = row[FIRST_CHOICE_COL + MAX_CHOICES + 2]?.toString().trim() || '';
          const tags = row[FIRST_CHOICE_COL + MAX_CHOICES + 3]?.toString().trim() || '';

          // Read image filenames and widths (optional columns, alternating: image, width, image, width, ...)
          const questionImageFilename = row[QUESTION_IMAGE_COL]?.toString().trim() || '';
          const questionImageWidth = row[QUESTION_IMAGE_WIDTH_COL]?.toString().trim() || '250';
          const choiceImageFilenames = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_IMAGE_COL + i * 2]?.toString().trim() || '');
          const choiceImageWidths = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_IMAGE_COL + i * 2 + 1]?.toString().trim() || '150');
          const explanationImageFilename = row[EXPLANATION_IMAGE_COL]?.toString().trim() || '';
          const explanationImageWidth = row[EXPLANATION_IMAGE_WIDTH_COL]?.toString().trim() || '250';

          // Skip rows with blank question text
          if (!questionText) {
            return;
          }

          // Validation
          const rowErrors = [];

          // Validate image filenames exist in ZIP images (only if filenames are provided)
          if (questionImageFilename && Object.keys(zipImages).length > 0 && !zipImages[questionImageFilename.toLowerCase()]) {
            rowErrors.push(`Question image "${questionImageFilename}" not found in ZIP images folder`);
          }
          choiceImageFilenames.forEach((fn, i) => {
            if (fn && Object.keys(zipImages).length > 0 && !zipImages[fn.toLowerCase()]) {
              rowErrors.push(`Choice ${CHOICE_LABELS[i]} image "${fn}" not found in ZIP images folder`);
            }
          });
          if (explanationImageFilename && Object.keys(zipImages).length > 0 && !zipImages[explanationImageFilename.toLowerCase()]) {
            rowErrors.push(`Explanation image "${explanationImageFilename}" not found in ZIP images folder`);
          }

          if (!subjectName) rowErrors.push('Subject Name is required');
          if (!unitName) rowErrors.push('Unit Name is required');
          if (!choiceTexts[0] && !choiceImageFilenames[0]) rowErrors.push('Choice A is required (text or image)');
          if (!choiceTexts[1] && !choiceImageFilenames[1]) rowErrors.push('Choice B is required (text or image)');
          if (!CHOICE_LABELS.includes(correctAnswer)) {
            rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
          } else {
            // Validate correct answer references a non-empty choice (text or image)
            const correctIdx = CHOICE_LABELS.indexOf(correctAnswer);
            if (!choiceTexts[correctIdx] && !choiceImageFilenames[correctIdx]) {
              rowErrors.push(`Correct Answer is ${correctAnswer} but Choice ${correctAnswer} is empty`);
            }
          }

          // Find matching subject - match by name or by "CODE Name" format
          const matchedSubject = subjects.find(s => {
            const sLower = subjectName.toLowerCase();
            const nameLower = s.name?.toLowerCase() || '';
            const displayName = s.code ? `${s.code} ${s.name}`.toLowerCase() : nameLower;
            return nameLower === sLower || displayName === sLower;
          });

          // Find matching unit - match by name or by "CODE Name" format
          let matchedUnit = units?.find(u => {
            const uLower = unitName.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return (nameLower === uLower || displayName === uLower) &&
                   matchedSubject && u.subjectId === matchedSubject?.subjectId;
          });

          // If unit not found for this subject, check if it exists under another subject
          const unitExistsElsewhere = !matchedUnit && units?.find(u => {
            const uLower = unitName.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return nameLower === uLower || displayName === uLower;
          });

          if (unitExistsElsewhere && matchedSubject) {
            // Unit exists but doesn't belong to selected subject
            rowErrors.push(`Unit "${unitName}" does not belong to Subject "${subjectName}". Please fix in the review.`);
          }

          // Find matching topic (optional) - match by name or by "CODE Name" format
          let matchedTopic = null;
          if (topicName && matchedUnit) {
            matchedTopic = topics?.find(t => {
              const tLower = topicName.toLowerCase();
              const nameLower = t.name?.toLowerCase() || '';
              const displayName = t.code ? `${t.code} ${t.name}`.toLowerCase() : nameLower;
              return (nameLower === tLower || displayName === tLower) &&
                     t.unitId === matchedUnit?.unitId;
            });

            // Check if topic exists under another unit
            const topicExistsElsewhere = !matchedTopic && topics?.find(t => {
              const tLower = topicName.toLowerCase();
              const nameLower = t.name?.toLowerCase() || '';
              const displayName = t.code ? `${t.code} ${t.name}`.toLowerCase() : nameLower;
              return nameLower === tLower || displayName === tLower;
            });

            if (topicExistsElsewhere) {
              rowErrors.push(`Topic "${topicName}" does not belong to Unit "${unitName}". Please fix in the review.`);
            }
          }

          // Match passage by title (optional)
          let matchedPassage = null;
          if (passageTitle && matchedSubject && matchedUnit) {
            matchedPassage = passages?.find(p =>
              p.title?.toLowerCase() === passageTitle.toLowerCase() &&
              p.subjectId === matchedSubject.subjectId &&
              p.unitId === matchedUnit.unitId
            );
            if (!matchedPassage) {
              // Try matching by title only (across all subjects/units)
              const anyPassage = passages?.find(p =>
                p.title?.toLowerCase() === passageTitle.toLowerCase()
              );
              if (anyPassage) {
                rowErrors.push(`Passage "${passageTitle}" exists but belongs to a different subject/unit. Please fix in the review.`);
              } else {
                rowErrors.push(`Passage "${passageTitle}" not found. Please create the passage first or fix the title.`);
              }
            }
          }

          if (rowErrors.length > 0) {
            errors.push({ row: rowNum, errors: rowErrors });
          }

          // Create blob URLs for image preview in review modal
          let questionImageBlobUrl = null;
          if (questionImageFilename && zipImages[questionImageFilename.toLowerCase()]) {
            questionImageBlobUrl = URL.createObjectURL(zipImages[questionImageFilename.toLowerCase()]);
          }
          const choiceImageBlobUrls = choiceImageFilenames.map(fn => {
            if (fn && zipImages[fn.toLowerCase()]) return URL.createObjectURL(zipImages[fn.toLowerCase()]);
            return null;
          });
          let explanationImageBlobUrl = null;
          if (explanationImageFilename && zipImages[explanationImageFilename.toLowerCase()]) {
            explanationImageBlobUrl = URL.createObjectURL(zipImages[explanationImageFilename.toLowerCase()]);
          }

          // Build choices array (include choices with text and/or image), embed images if present
          const choices = [];
          choiceTexts.forEach((text, i) => {
            if (text || choiceImageFilenames[i]) {
              const choiceBlobUrl = choiceImageBlobUrls[i];
              choices.push({
                choiceId: i.toString(),
                text: choiceBlobUrl ? textToDraftRawWithImage(text, choiceBlobUrl, choiceImageWidths[i]) : text,
                label: CHOICE_LABELS[i]
              });
            }
          });

          // Map correct answer to choice ID
          const correctChoiceId = CORRECT_ANSWER_MAP[correctAnswer];

          questions.push({
            rowNumber: rowNum,
            subjectName,
            unitName,
            topicName,
            passageTitle,
            passageId: matchedPassage?.passageId || null,
            subjectId: matchedSubject?.subjectId || null,
            unitId: matchedUnit?.unitId || null,
            topicId: matchedTopic?.topicId || null,
            difficulty: 'medium',  // Default difficulty since removed from template
            questionText: questionImageBlobUrl ? textToDraftRawWithImage(questionText, questionImageBlobUrl, questionImageWidth) : questionText,
            choices,
            correctAnswer: correctChoiceId ? [correctChoiceId] : [],
            correctAnswerLabel: correctAnswer,
            explanation: explanationImageBlobUrl ? textToDraftRawWithImage(explanation, explanationImageBlobUrl, explanationImageWidth) : explanation,
            lessonReference,
            tags: tags ? tags.split(',').map(t => t.trim()).filter(t => t) : [],
            questionImageFilename,
            choiceImageFilenames,
            explanationImageFilename,
            questionImageWidth,
            choiceImageWidths,
            explanationImageWidth,
            questionImageBlobUrl,
            choiceImageBlobUrls,
            explanationImageBlobUrl,
            hasValidationErrors: rowErrors.length > 0,
            validationErrors: rowErrors
          });
        });

        console.log('[PARSE] Result: questions:', questions.length, 'errors:', errors.length);
        setParsedQuestions(questions);
        setParseErrors(errors);

        if (questions.length > 0) {
          setIsReviewModalOpen(true);
        } else if (errors.length === 0) {
          setParseErrors(['No questions found. Make sure the "Question Text" column (column E) is filled in.']);
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        setParseErrors([`Error parsing file: ${error.message}`]);
      }
    };

    reader.readAsArrayBuffer(file);
  }, [subjects, units, topics, passages]);

  // Handle file input change
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls, .csv) or ZIP file.', 'error');
        return;
      }
      setFile(selectedFile);
      setParsedQuestions([]);
      setParseErrors([]);
      setUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(selectedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseFile(selectedFile);
      }
    }
  };

  // Handle drag and drop
  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileExtension = droppedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls, .csv) or ZIP file.', 'error');
        return;
      }
      setFile(droppedFile);
      setParsedQuestions([]);
      setParseErrors([]);
      setUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(droppedFile);
          setImageFiles(images);
          parseFile(excelFile, images);
        } catch (err) {
          setParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseFile(droppedFile);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Replace blob: URLs in Draft.js raw content entityMap with Firebase URLs
  const replaceBlobUrl = (raw, blobUrl, firebaseUrl) => {
    if (!raw || !raw.entityMap || !blobUrl || !firebaseUrl) return raw;
    const result = JSON.parse(JSON.stringify(raw));
    Object.values(result.entityMap).forEach(entity => {
      if (entity.type === 'IMAGE' && entity.data?.src === blobUrl) {
        entity.data.src = firebaseUrl;
      }
    });
    return result;
  };

  // Finalize Draft.js raw content for saving: replace blob URL or add Firebase image
  const finalizeRawContent = (content, blobUrl, firebaseUrl, width) => {
    let raw = typeof content === 'object' && content.blocks ? content : textToDraftRaw(content);
    if (blobUrl && firebaseUrl) {
      return replaceBlobUrl(raw, blobUrl, firebaseUrl);
    } else if (!blobUrl && firebaseUrl) {
      return textToDraftRawWithImage(content, firebaseUrl, width);
    }
    return raw;
  };

  // Convert plain text to Draft.js raw content state format
  // If already a Draft.js raw object, pass through unchanged
  const textToDraftRaw = (text) => {
    if (!text) return null;
    if (typeof text === 'object' && text.blocks) return text;
    return {
      blocks: [
        {
          key: Math.random().toString(36).substr(2, 5),
          text: text,
          type: 'unstyled',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {}
        }
      ],
      entityMap: {}
    };
  };

  // Save all questions
  const handleSaveQuestions = async (questionsToSave) => {
    setIsUploading(true);
    setUploadProgress(0);
    setIsReviewModalOpen(false);

    // Step 1: Upload images from ZIP if any
    const imageUrlMap = {};
    const allFilenames = new Set();
    questionsToSave.forEach(q => {
      if (q.questionImageFilename) allFilenames.add(q.questionImageFilename.toLowerCase());
      (q.choiceImageFilenames || []).forEach(fn => { if (fn) allFilenames.add(fn.toLowerCase()); });
      if (q.explanationImageFilename) allFilenames.add(q.explanationImageFilename.toLowerCase());
    });

    if (allFilenames.size > 0 && Object.keys(imageFiles).length > 0) {
      setIsUploadingImages(true);
      setImageUploadProgress(0);
      const filenames = Array.from(allFilenames);
      for (let i = 0; i < filenames.length; i++) {
        const fn = filenames[i];
        const imgFile = imageFiles[fn];
        if (imgFile) {
          try {
            const optimized = await optimizeImage(imgFile, { maxW: 800, quality: 0.8 });
            const url = await uploadImage(optimized);
            imageUrlMap[fn] = url;
          } catch (err) {
            console.error(`Failed to upload image ${fn}:`, err);
          }
        }
        setImageUploadProgress(Math.round(((i + 1) / filenames.length) * 100));
      }
      setIsUploadingImages(false);
    }

    // Generate a unique batch ID for this upload
    const bulkUploadId = `BATCH-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 6)}`;

    const results = {
      success: 0,
      failed: 0,
      errors: [],
      bulkUploadId
    };
    const questionCodes = [];

    for (let i = 0; i < questionsToSave.length; i++) {
      const question = questionsToSave[i];

      try {
        // Skip questions without required fields
        if (!question.subjectId || !question.unitId) {
          results.failed++;
          results.errors.push({
            row: question.rowNumber,
            error: 'Subject or Unit not matched. Please select valid values.'
          });
          continue;
        }

        // Resolve image URLs for this question
        const qImgUrl = imageUrlMap[question.questionImageFilename?.toLowerCase()] || null;
        const expImgUrl = imageUrlMap[question.explanationImageFilename?.toLowerCase()] || null;

        // Prepare question data for creation
        const questionData = {
          subjectId: question.subjectId,
          unitId: question.unitId,
          topicId: question.topicId || null,
          ...(question.passageId && { passageId: question.passageId, isPassageBased: true }),
          difficulty: question.difficulty,
          questionText: finalizeRawContent(question.questionText, question.questionImageBlobUrl, qImgUrl, question.questionImageWidth),
          choices: question.choices.map(c => {
            const choiceIdx = parseInt(c.choiceId);
            const cImgUrl = imageUrlMap[(question.choiceImageFilenames || [])[choiceIdx]?.toLowerCase()] || null;
            const cBlobUrl = (question.choiceImageBlobUrls || [])[choiceIdx] || null;
            return { choiceId: c.choiceId, text: finalizeRawContent(c.text, cBlobUrl, cImgUrl, (question.choiceImageWidths || [])[choiceIdx]) };
          }),
          correctAnswer: question.correctAnswer,
          explanationText: finalizeRawContent(question.explanation, question.explanationImageBlobUrl, expImgUrl, question.explanationImageWidth),
          lessonReference: question.lessonReference ? textToDraftRaw(question.lessonReference) : null,
          tags: question.tags || [],
          source: 'bulk_upload',
          bulkUploadId
        };

        // Get subject name for code generation
        const subject = subjects.find(s => s.subjectId === question.subjectId);
        const subjectName = subject?.name || 'GEN';

        const result = await dispatch(createQuestion(questionData, userEmail, subjectName));

        if (result.success) {
          results.success++;
          if (result.questionCode) questionCodes.push(result.questionCode);
        } else {
          results.failed++;
          results.errors.push({
            row: question.rowNumber,
            error: result.error || 'Unknown error'
          });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          row: question.rowNumber,
          error: error.message
        });
      }

      // Update progress
      setUploadProgress(Math.round(((i + 1) / questionsToSave.length) * 100));
    }

    // Log this batch
    const { storagePath, downloadUrl } = file
      ? await uploadBatchFile(file, 'bulk_upload', bulkUploadId)
      : { storagePath: null, downloadUrl: null };
    await dispatch(saveUploadLog('bulk_upload', {
      batchId: bulkUploadId,
      fileName: file?.name || 'unknown',
      storagePath,
      downloadUrl,
      uploadedBy: userEmail,
      totalRows: questionsToSave.length,
      successCount: results.success,
      failCount: results.failed,
      errors: results.errors.slice(0, 50),
      questionCodes,
    }));

    setIsUploading(false);
    setUploadResults(results);

    // Show summary
    if (results.success > 0 && results.failed === 0) {
      Swal.fire('Success!', `All ${results.success} questions have been uploaded successfully.`, 'success');
    } else if (results.success > 0 && results.failed > 0) {
      Swal.fire('Partial Success', `${results.success} questions uploaded. ${results.failed} failed.`, 'warning');
    } else {
      Swal.fire('Upload Failed', `All ${results.failed} questions failed to upload.`, 'error');
    }

    // Reset state
    setFile(null);
    setParsedQuestions([]);
    document.getElementById('bulk-upload-input').value = '';
  };

  // Handle undo/delete entire batch
  const handleDeleteBatch = async (batchId, count) => {
    const confirm = await Swal.fire({
      title: 'Undo Batch Upload?',
      text: `This will archive all ${count} questions from batch ${batchId}. This action cannot be easily undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete batch',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    setIsDeletingBatch(true);
    const result = await dispatch(deleteQuestionsBatch(batchId));
    setIsDeletingBatch(false);

    if (result.success) {
      Swal.fire('Batch Deleted', `${result.deletedCount} question(s) archived.${result.failedCount > 0 ? ` ${result.failedCount} failed.` : ''}`, 'success');
      setUploadResults(null);
      dispatch(fetchQuestions());
    } else {
      Swal.fire('Error', result.error || 'Failed to delete batch.', 'error');
    }
  };

  // ========== BULK EDIT: Filter Logic ==========

  // Subject options with question counts
  const subjectOptions = useMemo(() => {
    if (!subjects) return [];
    return subjects
      .filter(s => s.isActive !== false)
      .map(s => ({
        value: s.subjectId,
        label: `${s.code ? `${s.code} ` : ''}${s.name} (${allQuestions?.filter(q => q.subjectId === s.subjectId).length || 0})`
      }));
  }, [subjects, allQuestions]);

  // Unit options filtered by selected subjects
  const unitOptions = useMemo(() => {
    if (!units) return [];
    return units
      .filter(u => u.isActive !== false && (selectedSubjects.length === 0 || selectedSubjects.includes(u.subjectId)))
      .map(u => ({
        value: u.unitId,
        label: `${u.code ? `${u.code} ` : ''}${u.name} (${allQuestions?.filter(q => q.unitId === u.unitId).length || 0})`
      }));
  }, [units, selectedSubjects, allQuestions]);

  // Topic options filtered by selected units
  const topicOptions = useMemo(() => {
    if (!topics) return [];
    return topics
      .filter(t => selectedUnits.length === 0 || selectedUnits.includes(t.unitId))
      .map(t => ({
        value: t.topicId,
        label: `${t.code ? `${t.code} ` : ''}${t.name} (${allQuestions?.filter(q => q.topicId === t.topicId).length || 0})`
      }));
  }, [topics, selectedUnits, allQuestions]);

  // Filter questions based on selected subjects/units/topics and no-topic filter
  const filteredQuestions = useMemo(() => {
    if (!allQuestions || allQuestions.length === 0) return [];
    let result = allQuestions;
    if (selectedSubjects.length > 0 || selectedUnits.length > 0 || selectedTopics.length > 0) {
      result = result.filter(question => {
        if (selectedTopics.length > 0) {
          if (question.topicId && selectedTopics.includes(question.topicId)) return true;
        }
        if (selectedUnits.length > 0) {
          if (question.unitId && selectedUnits.includes(question.unitId)) {
            const unitTopics = topics?.filter(t => t.unitId === question.unitId).map(t => t.topicId) || [];
            const hasSelectedTopicsInUnit = unitTopics.some(tid => selectedTopics.includes(tid));
            if (!hasSelectedTopicsInUnit || !question.topicId) return true;
          }
        }
        if (selectedSubjects.length > 0) {
          if (question.subjectId && selectedSubjects.includes(question.subjectId)) {
            const subjectUnits = units?.filter(u => u.subjectId === question.subjectId).map(u => u.unitId) || [];
            const hasSelectedUnitsInSubject = subjectUnits.some(uid => selectedUnits.includes(uid));
            if (!hasSelectedUnitsInSubject || !question.unitId) return true;
          }
        }
        return false;
      });
    }
    if (filterNoTopic) {
      result = result.filter(question => !question.topicId);
    }
    return result;
  }, [allQuestions, selectedSubjects, selectedUnits, selectedTopics, filterNoTopic, units, topics]);

  // Handle cascading filter changes
  const handleSubjectChange = (selected) => {
    const newSubjects = selected ? selected.map(s => s.value) : [];
    setSelectedSubjects(newSubjects);
    if (newSubjects.length > 0) {
      const validUnits = selectedUnits.filter(uid => {
        const unit = units?.find(u => u.unitId === uid);
        return unit && newSubjects.includes(unit.subjectId);
      });
      setSelectedUnits(validUnits);
      const validTopics = selectedTopics.filter(tid => {
        const topic = topics?.find(t => t.topicId === tid);
        return topic && validUnits.includes(topic.unitId);
      });
      setSelectedTopics(validTopics);
    }
  };

  const handleUnitChange = (selected) => {
    const newUnits = selected ? selected.map(u => u.value) : [];
    setSelectedUnits(newUnits);
    if (newUnits.length > 0) {
      const validTopics = selectedTopics.filter(tid => {
        const topic = topics?.find(t => t.topicId === tid);
        return topic && newUnits.includes(topic.unitId);
      });
      setSelectedTopics(validTopics);
    }
  };

  // Search-filtered questions (applies search on top of subject/unit/topic filter)
  const searchFilteredQuestions = useMemo(() => {
    if (!editSearchTerm) return filteredQuestions;
    const term = editSearchTerm.toLowerCase();
    return filteredQuestions.filter(q => {
      const qText = typeof q.questionText === 'string'
        ? q.questionText
        : JSON.stringify(q.questionText || '');
      return qText.toLowerCase().includes(term) ||
        q.questionCode?.toLowerCase().includes(term) ||
        q.questionId?.toLowerCase().includes(term);
    });
  }, [filteredQuestions, editSearchTerm]);

  // Reset page when filters/search change
  useEffect(() => {
    setEditCurrentPage(1);
  }, [editSearchTerm, selectedSubjects, selectedUnits, selectedTopics, filterNoTopic]);

  // Auto-select all filtered questions when filters change
  useEffect(() => {
    if (filteredQuestions.length > 0) {
      setSelectedQuestionIds(filteredQuestions.map(q => q.questionId));
    } else {
      setSelectedQuestionIds([]);
    }
  }, [filteredQuestions]);

  // Paginated questions for the table
  const paginatedEditQuestions = useMemo(() => {
    const start = (editCurrentPage - 1) * editQuestionsPerPage;
    return searchFilteredQuestions.slice(start, start + editQuestionsPerPage);
  }, [searchFilteredQuestions, editCurrentPage]);

  const editTotalPages = Math.ceil(searchFilteredQuestions.length / editQuestionsPerPage);

  // Helper to get display names
  const getSubjectDisplay = (subjectId) => {
    const s = subjects?.find(s => s.subjectId === subjectId);
    return s ? (s.code ? `${s.code} ${s.name}` : s.name) : '';
  };
  const getUnitDisplay = (unitId) => {
    const u = (units || []).find(u => u.unitId === unitId);
    return u ? (u.code ? `${u.code} ${u.name}` : u.name) : '';
  };
  const getTopicDisplay = (topicId) => {
    if (!topicId) return '';
    const t = (topics || []).find(t => t.topicId === topicId);
    return t ? (t.code ? `${t.code} ${t.name}` : t.name) : '';
  };

  // Helper to extract plain text from Draft.js
  const getPlainText = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    return draftRawToPlainText(content);
  };

  // Toggle single question selection
  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]
    );
  };

  // Select all currently search-filtered questions
  const selectAllFiltered = () => {
    const allIds = searchFilteredQuestions.map(q => q.questionId);
    setSelectedQuestionIds(prev => {
      const combined = new Set([...prev, ...allIds]);
      return Array.from(combined);
    });
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedQuestionIds([]);
  };

  // ========== BULK EDIT: Archive Selected ==========
  const handleArchiveSelected = async () => {
    if (selectedQuestionIds.length === 0) {
      Swal.fire('No Questions Selected', 'Please select at least one question to archive.', 'warning');
      return;
    }

    const confirm = await Swal.fire({
      title: `Archive ${selectedQuestionIds.length} Question${selectedQuestionIds.length > 1 ? 's' : ''}?`,
      text: 'This will archive the selected questions and remove them from all question lists and test creation. Existing tests are not affected.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      confirmButtonText: `Yes, archive ${selectedQuestionIds.length > 1 ? 'them' : 'it'}`,
      cancelButtonText: 'Cancel'
    });

    if (!confirm.isConfirmed) return;

    setIsArchiving(true);
    let archived = 0;
    let failed = 0;
    const errors = [];

    for (const questionId of selectedQuestionIds) {
      const result = await dispatch(deleteQuestion(questionId));
      if (result.success) {
        archived++;
      } else {
        failed++;
        errors.push({ questionId, error: result.error });
      }
    }

    setIsArchiving(false);
    setSelectedQuestionIds([]);

    if (failed === 0) {
      Swal.fire('Archived!', `${archived} question${archived > 1 ? 's' : ''} archived successfully.`, 'success');
    } else {
      Swal.fire('Partial Success', `${archived} archived, ${failed} failed.`, 'warning');
    }
  };

  // Questions to export (selected only)
  const questionsToExport = useMemo(() => {
    if (!allQuestions) return [];
    return allQuestions.filter(q => selectedQuestionIds.includes(q.questionId));
  }, [allQuestions, selectedQuestionIds]);

  // ========== BULK EDIT: Export Function ==========

  const exportQuestions = async () => {
    if (questionsToExport.length === 0) {
      Swal.fire('No Questions Selected', 'Please select at least one question to export.', 'warning');
      return;
    }

    setIsExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'PMR Prep';
      workbook.created = new Date();

      // ===== Build dropdown lists data (same as download template) =====
      const subjectsList = subjects.map(s => ({
        name: s.name,
        code: s.code || '',
        displayName: s.code ? `${s.code} ${s.name}` : s.name
      })).filter(s => s.name).sort((a, b) => a.name.localeCompare(b.name));
      const subjectDisplayNames = subjectsList.map(s => s.displayName);

      const unitsList = (units || []).map(u => {
        const subject = subjects.find(s => s.subjectId === u.subjectId);
        return {
          name: u.name,
          code: u.code || '',
          displayName: u.code ? `${u.code} ${u.name}` : u.name,
          subjectDisplayName: subject?.code ? `${subject.code} ${subject.name}` : (subject?.name || '')
        };
      }).filter(u => u.name);

      const topicsList = (topics || []).map(t => {
        const unit = (units || []).find(u => u.unitId === t.unitId);
        return {
          name: t.name,
          code: t.code || '',
          displayName: t.code ? `${t.code} ${t.name}` : t.name,
          unitDisplayName: unit?.code ? `${unit.code} ${unit.name}` : (unit?.name || '')
        };
      }).filter(t => t.name);

      const unitsBySubject = {};
      unitsList.forEach(u => {
        if (!unitsBySubject[u.subjectDisplayName]) unitsBySubject[u.subjectDisplayName] = [];
        unitsBySubject[u.subjectDisplayName].push(u.displayName);
      });

      const topicsByUnit = {};
      topicsList.forEach(t => {
        if (!topicsByUnit[t.unitDisplayName]) topicsByUnit[t.unitDisplayName] = [];
        topicsByUnit[t.unitDisplayName].push(t.displayName);
      });

      // ===== Create hidden Lists sheet with named ranges =====
      const listsSheet = workbook.addWorksheet('Lists');
      listsSheet.state = 'veryHidden';

      let currentCol = 1;
      subjectDisplayNames.forEach((displayName, i) => {
        listsSheet.getCell(i + 1, currentCol).value = displayName;
      });
      workbook.definedNames.add(`Lists!$A$1:$A$${subjectDisplayNames.length}`, 'SubjectsList');

      currentCol = 2;
      Object.keys(unitsBySubject).sort().forEach(subjectDisplayName => {
        const unitsForSubject = unitsBySubject[subjectDisplayName];
        unitsForSubject.forEach((unitDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = unitDisplayName;
        });
        const rangeName = sanitizeNameForExcel(subjectDisplayName);
        const colLetter = String.fromCharCode(64 + currentCol);
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${unitsForSubject.length}`, rangeName);
        currentCol++;
      });

      Object.keys(topicsByUnit).sort().forEach(unitDisplayName => {
        const topicsForUnit = topicsByUnit[unitDisplayName];
        topicsForUnit.forEach((topicDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = topicDisplayName;
        });
        const rangeName = sanitizeNameForExcel('T_' + unitDisplayName);
        let colLetter;
        if (currentCol <= 26) {
          colLetter = String.fromCharCode(64 + currentCol);
        } else {
          const first = Math.floor((currentCol - 1) / 26);
          const second = ((currentCol - 1) % 26) + 1;
          colLetter = String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
        }
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${topicsForUnit.length}`, rangeName);
        currentCol++;
      });

      // ===== Create Questions Export sheet =====
      const sheet = workbook.addWorksheet('Questions Export');

      const headers = [
        'Question Code',
        'Subject Name',
        'Unit Name',
        'Topic Name (Optional)',
        'Passage Title (Optional)',
        'Question Text',
        ...CHOICE_LABELS.map(l => `Choice ${l}`),
        'Correct Answer',
        'Explanation',
        'Lesson Reference (Optional)',
        'Tags (comma-separated, optional)',
        'Question Image (filename)',
        'Question Image Width (px)',
        ...CHOICE_LABELS.flatMap(l => [`Choice ${l} Image (filename)`, `Choice ${l} Image Width (px)`]),
        'Explanation Image (filename)',
        'Explanation Image Width (px)'
      ];

      const exportImageColStart = headers.indexOf('Question Image (filename)');

      sheet.getRow(1).values = headers;
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      sheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };

      // Style image columns with orange header
      const exportHeaderRow = sheet.getRow(1);
      for (let ci = exportImageColStart; ci < headers.length; ci++) {
        const cell = exportHeaderRow.getCell(ci + 1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED7D31' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      sheet.columns = [
        { width: 22 },  // Question Code
        { width: 22 },  // Subject
        { width: 28 },  // Unit
        { width: 22 },  // Topic
        { width: 30 },  // Passage Title
        { width: 50 },  // Question Text
        ...CHOICE_LABELS.map(() => ({ width: 30 })),  // Choices A-J
        { width: 15 },  // Correct Answer
        { width: 50 },  // Explanation
        { width: 40 },  // Lesson Reference
        { width: 25 },  // Tags
        { width: 28 }, { width: 18 },  // Question Image + Width
        ...CHOICE_LABELS.flatMap(() => [{ width: 22 }, { width: 18 }]),  // Choice A-J Image + Width
        { width: 28 }, { width: 18 }   // Explanation Image + Width
      ];

      // Helper to get correct answer letter
      const getCorrectAnswerLabel = (question) => {
        const correctIds = question.correctAnswer || [];
        if (correctIds.length === 0) return '';
        return ID_TO_LABEL_MAP[correctIds[0]] || '';
      };

      // Populate rows with data
      questionsToExport.forEach((question, i) => {
        const row = sheet.getRow(i + 2);
        const choices = question.choices || [];
        const passageObj = question.passageId ? passages?.find(p => p.passageId === question.passageId) : null;
        row.values = [
          question.questionCode || '',
          getSubjectDisplay(question.subjectId),
          getUnitDisplay(question.unitId),
          getTopicDisplay(question.topicId),
          passageObj?.title || '',
          getPlainText(question.questionText),
          ...CHOICE_LABELS.map((_, idx) => getPlainText(choices[idx]?.text)),
          getCorrectAnswerLabel(question),
          getPlainText(question.explanationText),
          getPlainText(question.lessonReference),
          (question.tags || []).join(', ')
        ];
      });

      // ===== Add cascading dropdown validation =====
      // Apply to exported rows + extra rows for new questions user might add
      const lastValidationRow = questionsToExport.length + 50;
      for (let rowNum = 2; rowNum <= lastValidationRow; rowNum++) {
        // Subject dropdown (Column B)
        sheet.getCell(`B${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: ['SubjectsList'],
          showErrorMessage: true,
          errorTitle: 'Invalid Subject',
          error: 'Please select a subject from the dropdown list'
        };

        // Unit dropdown (Column C) - cascading from Subject in B
        sheet.getCell(`C${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`INDIRECT("R_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(B${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
          showErrorMessage: true,
          errorTitle: 'Invalid Unit',
          error: 'Please select a unit from the dropdown. Make sure to select a Subject first!'
        };

        // Topic dropdown (Column D) - cascading from Unit in C
        if (Object.keys(topicsByUnit).length > 0) {
          sheet.getCell(`D${rowNum}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`INDIRECT("R_T_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(C${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
            showErrorMessage: true,
            errorTitle: 'Invalid Topic',
            error: 'Please select a topic from the dropdown or leave blank. Make sure to select a Unit first!'
          };
        }

        // Correct Answer dropdown
        const exportCorrectAnswerCol = String.fromCharCode(65 + FIRST_CHOICE_COL_EXPORT + MAX_CHOICES); // Column letter for Correct Answer in export
        sheet.getCell(`${exportCorrectAnswerCol}${rowNum}`).dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${ANSWER_DROPDOWN_STRING}"`],
          showErrorMessage: true,
          errorTitle: 'Invalid Answer',
          error: `Please select ${ANSWER_DROPDOWN_STRING}`
        };
      }

      // Generate and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `questions_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire('Exported!', `${questionsToExport.length} questions exported successfully. Edit the file and re-upload below to apply changes.`, 'success');
    } catch (error) {
      console.error('Error exporting questions:', error);
      Swal.fire('Error', 'Failed to export questions. Please try again.', 'error');
    }

    setIsExporting(false);
  };

  // ========== BULK EDIT: Parse Uploaded Edit File ==========

  const parseEditFile = useCallback((file, zipImages = {}) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        let sheetName = workbook.SheetNames.find(name =>
          name.toLowerCase().includes('questions') || name.toLowerCase().includes('export')
        ) || workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setEditParseErrors(['File is empty or contains only headers']);
          return;
        }

        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
        const errors = [];
        const questions = [];

        rows.forEach((row, rowIndex) => {
          const rowNum = rowIndex + 2;

          // Column mapping for edit template (col 0 = Question Code, col 4 = Passage Title)
          const questionCode = row[0]?.toString().trim() || '';
          const subjectRaw = row[1]?.toString().trim() || '';
          const unitRaw = row[2]?.toString().trim() || '';
          const topicRaw = row[3]?.toString().trim() || '';
          const passageTitle = row[4]?.toString().trim() || '';
          const questionText = row[5]?.toString().trim() || '';
          // Read choices dynamically (columns 6 through 6+MAX_CHOICES-1)
          const choiceTexts = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_COL_EXPORT + i]?.toString().trim() || '');
          const correctAnswer = row[FIRST_CHOICE_COL_EXPORT + MAX_CHOICES]?.toString().trim().toUpperCase() || '';
          const explanation = row[FIRST_CHOICE_COL_EXPORT + MAX_CHOICES + 1]?.toString().trim() || '';
          const lessonReference = row[FIRST_CHOICE_COL_EXPORT + MAX_CHOICES + 2]?.toString().trim() || '';
          const tags = row[FIRST_CHOICE_COL_EXPORT + MAX_CHOICES + 3]?.toString().trim() || '';

          // Read image filenames and widths (optional columns, alternating: image, width, image, width, ...)
          const questionImageFilename = row[QUESTION_IMAGE_COL_EXPORT]?.toString().trim() || '';
          const questionImageWidth = row[QUESTION_IMAGE_WIDTH_COL_EXPORT]?.toString().trim() || '250';
          const choiceImageFilenames = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_IMAGE_COL_EXPORT + i * 2]?.toString().trim() || '');
          const choiceImageWidths = CHOICE_LABELS.map((_, i) => row[FIRST_CHOICE_IMAGE_COL_EXPORT + i * 2 + 1]?.toString().trim() || '150');
          const explanationImageFilename = row[EXPLANATION_IMAGE_COL_EXPORT]?.toString().trim() || '';
          const explanationImageWidth = row[EXPLANATION_IMAGE_WIDTH_COL_EXPORT]?.toString().trim() || '250';

          if (!questionText) return;

          const rowErrors = [];

          // Validate image filenames exist in ZIP images
          if (questionImageFilename && Object.keys(zipImages).length > 0 && !zipImages[questionImageFilename.toLowerCase()]) {
            rowErrors.push(`Question image "${questionImageFilename}" not found in ZIP images folder`);
          }
          choiceImageFilenames.forEach((fn, i) => {
            if (fn && Object.keys(zipImages).length > 0 && !zipImages[fn.toLowerCase()]) {
              rowErrors.push(`Choice ${CHOICE_LABELS[i]} image "${fn}" not found in ZIP images folder`);
            }
          });
          if (explanationImageFilename && Object.keys(zipImages).length > 0 && !zipImages[explanationImageFilename.toLowerCase()]) {
            rowErrors.push(`Explanation image "${explanationImageFilename}" not found in ZIP images folder`);
          }

          if (!subjectRaw) rowErrors.push('Subject Name is required');
          if (!unitRaw) rowErrors.push('Unit Name is required');
          if (!choiceTexts[0] && !choiceImageFilenames[0]) rowErrors.push('Choice A is required (text or image)');
          if (!choiceTexts[1] && !choiceImageFilenames[1]) rowErrors.push('Choice B is required (text or image)');
          if (!CHOICE_LABELS.includes(correctAnswer)) {
            rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
          } else {
            const correctIdx = CHOICE_LABELS.indexOf(correctAnswer);
            if (!choiceTexts[correctIdx] && !choiceImageFilenames[correctIdx]) {
              rowErrors.push(`Correct Answer is ${correctAnswer} but Choice ${correctAnswer} is empty`);
            }
          }

          // Match subject
          const matchedSubject = subjects.find(s => {
            const sLower = subjectRaw.toLowerCase();
            const nameLower = s.name?.toLowerCase() || '';
            const displayName = s.code ? `${s.code} ${s.name}`.toLowerCase() : nameLower;
            return nameLower === sLower || displayName === sLower;
          });

          // Match unit
          let matchedUnit = units?.find(u => {
            const uLower = unitRaw.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return (nameLower === uLower || displayName === uLower) &&
                   matchedSubject && u.subjectId === matchedSubject?.subjectId;
          });

          const unitExistsElsewhere = !matchedUnit && units?.find(u => {
            const uLower = unitRaw.toLowerCase();
            const nameLower = u.name?.toLowerCase() || '';
            const displayName = u.code ? `${u.code} ${u.name}`.toLowerCase() : nameLower;
            return nameLower === uLower || displayName === uLower;
          });

          if (unitExistsElsewhere && matchedSubject) {
            rowErrors.push(`Unit "${unitRaw}" does not belong to Subject "${subjectRaw}".`);
          }

          // Match topic
          let matchedTopic = null;
          if (topicRaw && matchedUnit) {
            matchedTopic = topics?.find(t => {
              const tLower = topicRaw.toLowerCase();
              const nameLower = t.name?.toLowerCase() || '';
              const displayName = t.code ? `${t.code} ${t.name}`.toLowerCase() : nameLower;
              return (nameLower === tLower || displayName === tLower) &&
                     t.unitId === matchedUnit?.unitId;
            });
          }

          // Match passage by title (optional)
          let matchedPassage = null;
          if (passageTitle && matchedSubject && matchedUnit) {
            matchedPassage = passages?.find(p =>
              p.title?.toLowerCase() === passageTitle.toLowerCase() &&
              p.subjectId === matchedSubject.subjectId &&
              p.unitId === matchedUnit.unitId
            );
            if (!matchedPassage) {
              const anyPassage = passages?.find(p =>
                p.title?.toLowerCase() === passageTitle.toLowerCase()
              );
              if (anyPassage) {
                rowErrors.push(`Passage "${passageTitle}" exists but belongs to a different subject/unit.`);
              } else {
                rowErrors.push(`Passage "${passageTitle}" not found.`);
              }
            }
          }

          // Look up existing question by code
          let existingQuestion = null;
          let existingQuestionId = null;
          let existingQuestionCode = null;
          if (questionCode && allQuestions) {
            existingQuestion = allQuestions.find(q => q.questionCode === questionCode);
            if (existingQuestion) {
              existingQuestionId = existingQuestion.questionId;
              existingQuestionCode = existingQuestion.questionCode;
            } else {
              rowErrors.push(`Question Code "${questionCode}" not found in database.`);
            }
          }

          if (rowErrors.length > 0) {
            errors.push({ row: rowNum, errors: rowErrors });
          }

          // Create blob URLs for image preview in review modal
          let questionImageBlobUrl = null;
          if (questionImageFilename && zipImages[questionImageFilename.toLowerCase()]) {
            questionImageBlobUrl = URL.createObjectURL(zipImages[questionImageFilename.toLowerCase()]);
          }
          const choiceImageBlobUrls = choiceImageFilenames.map(fn => {
            if (fn && zipImages[fn.toLowerCase()]) return URL.createObjectURL(zipImages[fn.toLowerCase()]);
            return null;
          });
          let explanationImageBlobUrl = null;
          if (explanationImageFilename && zipImages[explanationImageFilename.toLowerCase()]) {
            explanationImageBlobUrl = URL.createObjectURL(zipImages[explanationImageFilename.toLowerCase()]);
          }

          // Build choices (include choices with text and/or image), embed images if present
          const choices = [];
          choiceTexts.forEach((text, i) => {
            if (text || choiceImageFilenames[i]) {
              const choiceBlobUrl = choiceImageBlobUrls[i];
              choices.push({
                choiceId: i.toString(),
                text: choiceBlobUrl ? textToDraftRawWithImage(text, choiceBlobUrl, choiceImageWidths[i]) : text,
                label: CHOICE_LABELS[i]
              });
            }
          });

          const correctChoiceId = CORRECT_ANSWER_MAP[correctAnswer];

          // Detect changes for update questions
          let changes = [];
          if (existingQuestion) {
            const getPlainText = (content) => {
              if (!content) return '';
              if (typeof content === 'string') return content;
              try {
                return draftRawToPlainText(content);
              } catch { return ''; }
            };

            const oldSubject = subjects.find(s => s.subjectId === existingQuestion.subjectId);
            const oldUnit = (units || []).find(u => u.unitId === existingQuestion.unitId);
            const oldTopic = (topics || []).find(t => t.topicId === existingQuestion.topicId);
            const oldSubjectDisplay = oldSubject ? (oldSubject.code ? `${oldSubject.code} ${oldSubject.name}` : oldSubject.name) : '';
            const oldUnitDisplay = oldUnit ? (oldUnit.code ? `${oldUnit.code} ${oldUnit.name}` : oldUnit.name) : '';
            const oldTopicDisplay = oldTopic ? (oldTopic.code ? `${oldTopic.code} ${oldTopic.name}` : oldTopic.name) : '';

            if (subjectRaw && subjectRaw !== oldSubjectDisplay) changes.push({ field: 'Subject', oldValue: oldSubjectDisplay, newValue: subjectRaw });
            if (unitRaw && unitRaw !== oldUnitDisplay) changes.push({ field: 'Unit', oldValue: oldUnitDisplay, newValue: unitRaw });
            if (topicRaw !== oldTopicDisplay) changes.push({ field: 'Topic', oldValue: oldTopicDisplay, newValue: topicRaw || '(empty)' });

            const oldQuestionText = getPlainText(existingQuestion.questionText);
            if (questionText && questionText !== oldQuestionText) changes.push({ field: 'Question Text', oldValue: oldQuestionText.substring(0, 50) + '...', newValue: questionText.substring(0, 50) + '...' });

            const oldCorrect = ID_TO_LABEL_MAP[(existingQuestion.correctAnswer || [])[0]] || '';
            if (correctAnswer && correctAnswer !== oldCorrect) changes.push({ field: 'Correct Answer', oldValue: oldCorrect, newValue: correctAnswer });

            const oldExplanation = getPlainText(existingQuestion.explanationText);
            if (explanation !== oldExplanation) changes.push({ field: 'Explanation', oldValue: oldExplanation.substring(0, 50) + (oldExplanation.length > 50 ? '...' : ''), newValue: explanation.substring(0, 50) + (explanation.length > 50 ? '...' : '') });

            const oldTags = (existingQuestion.tags || []).join(', ');
            if (tags !== oldTags) changes.push({ field: 'Tags', oldValue: oldTags, newValue: tags });
          }

          questions.push({
            rowNumber: rowNum,
            questionCode,
            existingQuestionId,
            existingQuestionCode,
            existingQuestion,
            changes,
            subjectName: subjectRaw,
            unitName: unitRaw,
            topicName: topicRaw,
            passageTitle,
            passageId: matchedPassage?.passageId || existingQuestion?.passageId || null,
            subjectId: matchedSubject?.subjectId || null,
            unitId: matchedUnit?.unitId || null,
            topicId: matchedTopic?.topicId || null,
            difficulty: existingQuestion?.difficulty || 'medium',
            questionText: questionImageBlobUrl ? textToDraftRawWithImage(questionText, questionImageBlobUrl, questionImageWidth) : questionText,
            choices,
            correctAnswer: correctChoiceId ? [correctChoiceId] : [],
            correctAnswerLabel: correctAnswer,
            explanation: explanationImageBlobUrl ? textToDraftRawWithImage(explanation, explanationImageBlobUrl, explanationImageWidth) : explanation,
            lessonReference,
            tags: tags ? tags.split(',').map(t => t.trim()).filter(t => t) : [],
            questionImageFilename,
            choiceImageFilenames,
            explanationImageFilename,
            questionImageWidth,
            choiceImageWidths,
            explanationImageWidth,
            questionImageBlobUrl,
            choiceImageBlobUrls,
            explanationImageBlobUrl,
            hasValidationErrors: rowErrors.length > 0,
            validationErrors: rowErrors
          });
        });

        setEditParsedQuestions(questions);
        setEditParseErrors(errors);

        if (questions.length > 0) {
          setIsEditReviewModalOpen(true);
        }
      } catch (error) {
        console.error('Error parsing edit file:', error);
        setEditParseErrors([`Error parsing file: ${error.message}`]);
      }
    };

    reader.readAsArrayBuffer(file);
  }, [subjects, units, topics, allQuestions, passages]);

  // Handle edit file input
  const handleEditFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls, .csv) or ZIP file.', 'error');
        return;
      }
      setEditFile(selectedFile);
      setEditParsedQuestions([]);
      setEditParseErrors([]);
      setEditUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(selectedFile);
          setImageFiles(images);
          parseEditFile(excelFile, images);
        } catch (err) {
          setEditParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseEditFile(selectedFile);
      }
    }
  };

  const handleEditDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileExtension = droppedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv', 'zip'].includes(fileExtension)) {
        Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls, .csv) or ZIP file.', 'error');
        return;
      }
      setEditFile(droppedFile);
      setEditParsedQuestions([]);
      setEditParseErrors([]);
      setEditUploadResults(null);

      if (fileExtension === 'zip') {
        try {
          const { excelFile, images } = await extractZip(droppedFile);
          setImageFiles(images);
          parseEditFile(excelFile, images);
        } catch (err) {
          setEditParseErrors([err.message]);
        }
      } else {
        setImageFiles({});
        parseEditFile(droppedFile);
      }
    }
  };

  // ========== BULK EDIT: Save (Update/Create) ==========

  const handleSaveEditQuestions = async (questionsToSave) => {
    setIsEditUploading(true);
    setEditUploadProgress(0);
    setIsEditReviewModalOpen(false);

    // Step 1: Upload images from ZIP if any
    const editImageUrlMap = {};
    const editFilenames = new Set();
    questionsToSave.forEach(q => {
      if (q.questionImageFilename) editFilenames.add(q.questionImageFilename.toLowerCase());
      (q.choiceImageFilenames || []).forEach(fn => { if (fn) editFilenames.add(fn.toLowerCase()); });
      if (q.explanationImageFilename) editFilenames.add(q.explanationImageFilename.toLowerCase());
    });

    if (editFilenames.size > 0 && Object.keys(imageFiles).length > 0) {
      setIsUploadingImages(true);
      setImageUploadProgress(0);
      const fns = Array.from(editFilenames);
      for (let i = 0; i < fns.length; i++) {
        const fn = fns[i];
        const imgFile = imageFiles[fn];
        if (imgFile) {
          try {
            const optimized = await optimizeImage(imgFile, { maxW: 800, quality: 0.8 });
            const url = await uploadImage(optimized);
            editImageUrlMap[fn] = url;
          } catch (err) {
            console.error(`Failed to upload image ${fn}:`, err);
          }
        }
        setImageUploadProgress(Math.round(((i + 1) / fns.length) * 100));
      }
      setIsUploadingImages(false);
    }

    const results = { success: 0, failed: 0, updated: 0, created: 0, errors: [] };
    const updatedQuestionCodes = [];

    for (let i = 0; i < questionsToSave.length; i++) {
      const question = questionsToSave[i];

      try {
        if (!question.subjectId || !question.unitId) {
          results.failed++;
          results.errors.push({ row: question.rowNumber, error: 'Subject or Unit not matched.' });
          continue;
        }

        // Resolve image URLs
        const qImgUrl = editImageUrlMap[question.questionImageFilename?.toLowerCase()] || null;
        const expImgUrl = editImageUrlMap[question.explanationImageFilename?.toLowerCase()] || null;

        if (question.existingQuestionId) {
          // UPDATE existing question
          const updateData = {
            subjectId: question.subjectId,
            unitId: question.unitId,
            topicId: question.topicId || null,
            ...(question.passageId ? { passageId: question.passageId, isPassageBased: true } : {}),
            difficulty: question.difficulty,
            questionText: finalizeRawContent(question.questionText, question.questionImageBlobUrl, qImgUrl, question.questionImageWidth),
            choices: question.choices.map(c => {
              const choiceIdx = parseInt(c.choiceId);
              const cImgUrl = editImageUrlMap[(question.choiceImageFilenames || [])[choiceIdx]?.toLowerCase()] || null;
              const cBlobUrl = (question.choiceImageBlobUrls || [])[choiceIdx] || null;
              return { choiceId: c.choiceId, text: finalizeRawContent(c.text, cBlobUrl, cImgUrl, (question.choiceImageWidths || [])[choiceIdx]) };
            }),
            correctAnswer: question.correctAnswer,
            explanationText: finalizeRawContent(question.explanation, question.explanationImageBlobUrl, expImgUrl, question.explanationImageWidth),
            lessonReference: question.lessonReference ? textToDraftRaw(question.lessonReference) : null,
            tags: question.tags || []
          };

          const result = await dispatch(updateQuestion(question.existingQuestionId, updateData));
          if (result.success) {
            results.success++;
            results.updated++;
            if (question.questionCode) updatedQuestionCodes.push(question.questionCode);
          } else {
            results.failed++;
            results.errors.push({ row: question.rowNumber, error: result.error || 'Update failed' });
          }
        } else {
          // CREATE new question
          const questionData = {
            subjectId: question.subjectId,
            unitId: question.unitId,
            topicId: question.topicId || null,
            ...(question.passageId ? { passageId: question.passageId, isPassageBased: true } : {}),
            difficulty: question.difficulty,
            questionText: finalizeRawContent(question.questionText, question.questionImageBlobUrl, qImgUrl, question.questionImageWidth),
            choices: question.choices.map(c => {
              const choiceIdx = parseInt(c.choiceId);
              const cImgUrl = editImageUrlMap[(question.choiceImageFilenames || [])[choiceIdx]?.toLowerCase()] || null;
              const cBlobUrl = (question.choiceImageBlobUrls || [])[choiceIdx] || null;
              return { choiceId: c.choiceId, text: finalizeRawContent(c.text, cBlobUrl, cImgUrl, (question.choiceImageWidths || [])[choiceIdx]) };
            }),
            correctAnswer: question.correctAnswer,
            explanationText: finalizeRawContent(question.explanation, question.explanationImageBlobUrl, expImgUrl, question.explanationImageWidth),
            lessonReference: question.lessonReference ? textToDraftRaw(question.lessonReference) : null,
            tags: question.tags || [],
            source: 'bulk_upload'
          };

          const subject = subjects.find(s => s.subjectId === question.subjectId);
          const subjectName = subject?.name || 'GEN';
          const result = await dispatch(createQuestion(questionData, userEmail, subjectName));
          if (result.success) {
            results.success++;
            results.created++;
          } else {
            results.failed++;
            results.errors.push({ row: question.rowNumber, error: result.error || 'Create failed' });
          }
        }
      } catch (error) {
        results.failed++;
        results.errors.push({ row: question.rowNumber, error: error.message });
      }

      setEditUploadProgress(Math.round(((i + 1) / questionsToSave.length) * 100));
    }

    setIsEditUploading(false);
    setEditUploadResults(results);

    // Auto-resolve feedback for questions that were actually updated
    let resolvedCount = 0;
    if (results.updated > 0 && updatedQuestionCodes.length > 0) {
      const resolveResult = await dispatch(bulkResolveFeedback(updatedQuestionCodes));
      if (resolveResult.success) {
        resolvedCount = resolveResult.resolvedCount;
      }
    }

    // Single combined notification
    const feedbackNote = resolvedCount > 0 ? `\n${resolvedCount} related feedback item(s) auto-resolved.` : '';
    if (results.success > 0 && results.failed === 0) {
      Swal.fire('Success!', `All ${results.success} questions saved (${results.updated} updated, ${results.created} created).${feedbackNote}`, 'success');
    } else if (results.success > 0 && results.failed > 0) {
      Swal.fire('Partial Success', `${results.success} saved (${results.updated} updated, ${results.created} created). ${results.failed} failed.${feedbackNote}`, 'warning');
    } else {
      Swal.fire('Failed', `All ${results.failed} questions failed.`, 'error');
    }

    // Refresh questions list after edits
    dispatch(fetchQuestions({ pageSize: 5000 }));

    setEditFile(null);
    setEditParsedQuestions([]);
    const editInput = document.getElementById('bulk-edit-upload-input');
    if (editInput) editInput.value = '';
  };

  return (
    <>
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                <i className="fas fa-file-upload mr-2 text-primary"></i>
                Bulk Upload Questions
              </h3>
            </Col>
            <Col className="text-right">
              <Button color="info" size="sm" onClick={downloadTemplate}>
                <i className="fas fa-download mr-2"></i>
                Download Template
              </Button>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {/* Instructions */}
          <Alert color="info" className="mb-4">
            <h5 className="alert-heading">
              <i className="fas fa-info-circle mr-2"></i>
              Instructions
            </h5>
            <ol className="mb-0 pl-3">
              <li>Download the template file using the button above</li>
              <li>Fill in your questions following the template format</li>
              <li>Subject and Unit names must match existing entries in the system</li>
              <li>Correct Answer should be {ANSWER_DROPDOWN_STRING}</li>
              <li>Upload your completed file below to review and save</li>
            </ol>
          </Alert>

          {/* File Upload Area */}
          <div
            className="border rounded p-5 text-center bg-light"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => document.getElementById('bulk-upload-input').click()}
          >
            <input
              type="file"
              id="bulk-upload-input"
              accept=".xlsx,.xls,.csv,.zip"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">
              {file ? file.name : 'Drop your file here or click to browse'}
            </h4>
            <p className="text-muted mb-0">
              Supported formats: .xlsx, .xls, .csv, .zip (for images)
            </p>
          </div>

          {/* Parse Errors */}
          {parseErrors.length > 0 && parsedQuestions.length === 0 && (
            <Alert color="danger" className="mt-3">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {parseErrors.map((err, i) => (
                <span key={i}>{typeof err === 'string' ? err : `Row ${err.row}: ${err.errors?.join(', ')}`}</span>
              ))}
            </Alert>
          )}

          {/* Image Upload Progress */}
          {isUploadingImages && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" color="warning" className="mr-2" />
                <span>Uploading images... {imageUploadProgress}%</span>
              </div>
              <Progress value={imageUploadProgress} color="warning" />
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" color="primary" className="mr-2" />
                <span>Uploading questions... {uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} color="primary" />
            </div>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <div className="mt-4">
              <Alert color={uploadResults.failed === 0 ? 'success' : 'warning'}>
                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    <strong>Upload Complete:</strong> {uploadResults.success} succeeded, {uploadResults.failed} failed
                  </span>
                  {uploadResults.success > 0 && uploadResults.bulkUploadId && (
                    <Button
                      color="danger"
                      size="sm"
                      disabled={isDeletingBatch}
                      onClick={() => handleDeleteBatch(uploadResults.bulkUploadId, uploadResults.success)}
                    >
                      {isDeletingBatch ? <><Spinner size="sm" className="mr-1" /> Deleting...</> : `Undo Batch (${uploadResults.success})`}
                    </Button>
                  )}
                </div>
              </Alert>
              {uploadResults.errors.length > 0 && (
                <div className="mt-2">
                  <h5>Errors:</h5>
                  <ul className="text-danger">
                    {uploadResults.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Review Modal (Create) */}
      <BulkUploadReviewModal
        isOpen={isReviewModalOpen}
        toggle={() => setIsReviewModalOpen(false)}
        questions={parsedQuestions}
        subjects={subjects}
        units={units}
        topics={topics}
        parseErrors={parseErrors}
        onSave={handleSaveQuestions}
        onUpdateQuestion={(index, updaterOrQuestion) => {
          setParsedQuestions(prev => {
            const updated = [...prev];
            updated[index] = typeof updaterOrQuestion === 'function'
              ? updaterOrQuestion(updated[index])
              : updaterOrQuestion;
            return updated;
          });
        }}
      />

      {/* ========== BULK EDIT SECTION ========== */}
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <h3 className="mb-0">
                <i className="fas fa-edit mr-2 text-info"></i>
                Bulk Edit Questions
              </h3>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Alert color="info" className="mb-4">
            <h5 className="alert-heading">
              <i className="fas fa-info-circle mr-2"></i>
              How Bulk Edit Works
            </h5>
            <ol className="mb-0 pl-3">
              <li>Use the filters below to select which questions to export</li>
              <li>Click <strong>Export</strong> to download them as an Excel file (includes Question Codes)</li>
              <li>Edit the spreadsheet — change topics, tags, explanations, etc.</li>
              <li>Re-upload the edited file below — the system matches by Question Code and <strong>updates</strong> existing questions</li>
            </ol>
          </Alert>

          {/* Filters */}
          <h5 className="mb-3">
            <i className="fas fa-filter mr-2"></i>
            Filter Questions
          </h5>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">Subjects</Label>
                <Select
                  isMulti
                  options={subjectOptions}
                  value={subjectOptions.filter(opt => selectedSubjects.includes(opt.value))}
                  onChange={handleSubjectChange}
                  placeholder="All subjects"
                  styles={customDropdownWithBorder}
                  isLoading={!subjects || subjects.length === 0}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">Units</Label>
                <Select
                  isMulti
                  options={unitOptions}
                  value={unitOptions.filter(opt => selectedUnits.includes(opt.value))}
                  onChange={handleUnitChange}
                  placeholder="All units"
                  styles={customDropdownWithBorder}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="form-control-label">Topics</Label>
                <Select
                  isMulti
                  options={topicOptions}
                  value={topicOptions.filter(opt => selectedTopics.includes(opt.value))}
                  onChange={(selected) => setSelectedTopics(selected ? selected.map(t => t.value) : [])}
                  placeholder="All topics"
                  styles={customDropdownWithBorder}
                />
              </FormGroup>
            </Col>
          </Row>

          <CustomInput
            type="checkbox"
            id="filterNoTopic"
            label="No Topic assigned"
            checked={filterNoTopic}
            onChange={(e) => setFilterNoTopic(e.target.checked)}
            className="mb-3"
          />

          {/* Search */}
          <InputGroup className="mb-3">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                <i className="fas fa-search"></i>
              </InputGroupText>
            </InputGroupAddon>
            <Input
              type="text"
              placeholder="Search by question code or text..."
              value={editSearchTerm}
              onChange={(e) => setEditSearchTerm(e.target.value)}
            />
          </InputGroup>

          {/* Actions Bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <Button color="primary" size="sm" onClick={selectAllFiltered} className="mr-2">
                <i className="fas fa-check-double mr-1"></i>
                Select All
              </Button>
              <Button color="secondary" size="sm" onClick={deselectAll} className="mr-2">
                <i className="fas fa-times mr-1"></i>
                Deselect All
              </Button>
              <span className="text-muted small d-none d-sm-inline">
                <strong>{selectedQuestionIds.length}</strong> of {searchFilteredQuestions.length} selected
              </span>
            </div>
            <div className="d-flex">
              <Button
                color="warning"
                size="sm"
                onClick={handleArchiveSelected}
                disabled={isArchiving || questionsLoading || selectedQuestionIds.length === 0}
                className="mr-2"
              >
                {isArchiving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Archiving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-archive mr-1"></i>
                    Archive {selectedQuestionIds.length > 0 ? selectedQuestionIds.length : ''}
                  </>
                )}
              </Button>
              <Button
                color="success"
                size="sm"
                onClick={exportQuestions}
                disabled={isExporting || questionsLoading || questionsToExport.length === 0}
              >
                {isExporting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-export mr-1"></i>
                    Export {questionsToExport.length}
                  </>
                )}
              </Button>
            </div>
          </div>
          {/* Selected count on mobile (below buttons) */}
          <div className="d-sm-none text-muted small mb-3">
            <strong>{selectedQuestionIds.length}</strong> of {searchFilteredQuestions.length} selected
          </div>

          {/* Questions Table */}
          {questionsLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading questions...</p>
            </div>
          ) : searchFilteredQuestions.length === 0 ? (
            editSearchTerm ? (
              <Alert color="warning" className="text-center">
                <i className="fas fa-search fa-2x mb-2 d-block"></i>
                No questions found matching "<strong>{editSearchTerm}</strong>". Try a different search term.
              </Alert>
            ) : (
              <Alert color="warning" className="text-center">
                <i className="fas fa-filter fa-2x mb-2 d-block"></i>
                No questions match the current filters. Try adjusting your subject, unit, or topic selection.
              </Alert>
            )
          ) : (
            <>
              <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: '40px', minWidth: '40px' }}>
                        <CustomInput
                          type="checkbox"
                          id="editSelectAllPage"
                          checked={paginatedEditQuestions.length > 0 && paginatedEditQuestions.every(q => selectedQuestionIds.includes(q.questionId))}
                          onChange={(e) => {
                            const pageIds = paginatedEditQuestions.map(q => q.questionId);
                            if (e.target.checked) {
                              setSelectedQuestionIds(prev => Array.from(new Set([...prev, ...pageIds])));
                            } else {
                              setSelectedQuestionIds(prev => prev.filter(id => !pageIds.includes(id)));
                            }
                          }}
                        />
                      </th>
                      <th style={{ minWidth: '120px' }}>Code</th>
                      <th style={{ minWidth: '200px' }}>Question</th>
                      <th className="d-none d-md-table-cell" style={{ minWidth: '100px' }}>Subject</th>
                      <th className="d-none d-md-table-cell" style={{ minWidth: '100px' }}>Unit</th>
                      <th className="d-none d-lg-table-cell" style={{ minWidth: '100px' }}>Topic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEditQuestions.map(question => (
                      <tr key={question.questionId}>
                        <td>
                          <CustomInput
                            type="checkbox"
                            id={`edit-q-${question.questionId}`}
                            checked={selectedQuestionIds.includes(question.questionId)}
                            onChange={() => toggleQuestionSelection(question.questionId)}
                          />
                        </td>
                        <td>
                          <small className="font-weight-bold text-primary">{question.questionCode || '-'}</small>
                        </td>
                        <td>
                          <small className="d-block" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                            {getPlainText(question.questionText).substring(0, 80) || '-'}
                          </small>
                          {/* Show subject/unit inline on small screens */}
                          <div className="d-md-none mt-1">
                            <small className="text-muted">
                              {getSubjectDisplay(question.subjectId)}
                              {getUnitDisplay(question.unitId) && <> / {getUnitDisplay(question.unitId)}</>}
                            </small>
                          </div>
                        </td>
                        <td className="d-none d-md-table-cell"><small>{getSubjectDisplay(question.subjectId)}</small></td>
                        <td className="d-none d-md-table-cell"><small>{getUnitDisplay(question.unitId)}</small></td>
                        <td className="d-none d-lg-table-cell"><small>{getTopicDisplay(question.topicId) || '-'}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {editTotalPages > 1 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                  <small className="text-muted mb-2 mb-sm-0">
                    Showing {((editCurrentPage - 1) * editQuestionsPerPage) + 1} to {Math.min(editCurrentPage * editQuestionsPerPage, searchFilteredQuestions.length)} of {searchFilteredQuestions.length}
                  </small>
                  <Pagination size="sm" className="mb-0 flex-wrap justify-content-center">
                    <PaginationItem disabled={editCurrentPage === 1}>
                      <PaginationLink previous onClick={() => setEditCurrentPage(p => Math.max(1, p - 1))} />
                    </PaginationItem>
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let start = Math.max(1, editCurrentPage - Math.floor(maxVisible / 2));
                      let end = Math.min(editTotalPages, start + maxVisible - 1);
                      if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <PaginationItem key={i} active={editCurrentPage === i}>
                            <PaginationLink onClick={() => setEditCurrentPage(i)}>{i}</PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return pages;
                    })()}
                    <PaginationItem disabled={editCurrentPage === editTotalPages}>
                      <PaginationLink next onClick={() => setEditCurrentPage(p => Math.min(editTotalPages, p + 1))} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </>
          )}

          <hr className="mt-4" />

          {/* Re-Upload for Edit */}
          <h5 className="mb-3">
            <i className="fas fa-upload mr-2"></i>
            Re-Upload Edited File
          </h5>
          <div
            className="border rounded p-5 text-center bg-light"
            onDrop={handleEditDrop}
            onDragOver={(e) => e.preventDefault()}
            style={{ cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => document.getElementById('bulk-edit-upload-input').click()}
          >
            <input
              type="file"
              id="bulk-edit-upload-input"
              accept=".xlsx,.xls,.csv,.zip"
              onChange={handleEditFileChange}
              style={{ display: 'none' }}
            />
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">
              {editFile ? editFile.name : 'Drop your edited export file here or click to browse'}
            </h4>
            <p className="text-muted mb-0">
              Supported formats: .xlsx, .xls, .csv, .zip (for images)
            </p>
          </div>

          {/* Edit Upload Progress */}
          {isEditUploading && (
            <div className="mt-4">
              <div className="d-flex align-items-center mb-2">
                <Spinner size="sm" color="primary" className="mr-2" />
                <span>Saving questions... {editUploadProgress}%</span>
              </div>
              <Progress value={editUploadProgress} color="primary" />
            </div>
          )}

          {/* Edit Upload Results */}
          {editUploadResults && (
            <div className="mt-4">
              <Alert color={editUploadResults.failed === 0 ? 'success' : 'warning'}>
                <strong>Save Complete:</strong>{' '}
                {editUploadResults.updated > 0 && <>{editUploadResults.updated} updated</>}
                {editUploadResults.updated > 0 && editUploadResults.created > 0 && ', '}
                {editUploadResults.created > 0 && <>{editUploadResults.created} created</>}
                {editUploadResults.failed > 0 && <>, {editUploadResults.failed} failed</>}
              </Alert>
              {editUploadResults.errors.length > 0 && (
                <div className="mt-2">
                  <h5>Errors:</h5>
                  <ul className="text-danger">
                    {editUploadResults.errors.map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Review Modal (Edit) */}
      <BulkUploadReviewModal
        isOpen={isEditReviewModalOpen}
        toggle={() => setIsEditReviewModalOpen(false)}
        questions={editParsedQuestions}
        subjects={subjects}
        units={units}
        topics={topics}
        parseErrors={editParseErrors}
        onSave={handleSaveEditQuestions}
        onUpdateQuestion={(index, updaterOrQuestion) => {
          setEditParsedQuestions(prev => {
            const updated = [...prev];
            updated[index] = typeof updaterOrQuestion === 'function'
              ? updaterOrQuestion(updated[index])
              : updaterOrQuestion;
            return updated;
          });
        }}
        mode="edit"
      />
    </>
  );
};

export default BulkUploadQuestions;
